<template>
  <v-app v-if="!accessToken" id="yywd-front">
    <head>
      <title>{{ pageTitle }}</title>
    </head>
    <v-main>
      <v-container class="fill-height" fluid>
        <v-row align="center" justify="center" class="my-16">
          <v-col cols="12" sm="8" md="4">
            <v-img
              :src="
                logo_url
                  ? logo_url
                  : require('./assets/' + $LogoFolder + $t('$assets.fullLogo'))
              "
              contain
            />
          </v-col>
          <v-col v-if="showLoginDialog" cols="12" sm="8" md="4">
            <v-card class="elevation-12">
              <v-toolbar color="primary" dark flat>
                <v-toolbar-title>
                  <span>
                    <v-img
                      :src="
                        require('./assets/' +
                          $LogoFolder +
                          $t('$assets.logoSmallWhite'))
                      "
                      contain
                      height="32"
                      width="32"
                      class="me-3"
                      :class="$vuetify.rtl ? 'float-right' : 'float-left'"
                    />
                    {{ appTitle }}
                  </span>
                </v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                <ValidationObserver ref="observer">
                  <v-form>
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('App.account')"
                      rules="required"
                    >
                      <v-text-field
                        name="account_id"
                        prepend-icon="fa-tag"
                        type="text"
                        id="account_id"
                        v-model="login.account_id"
                        :label="$t('App.account')"
                        :error-messages="login.accountErrors.concat(errors)"
                        :disabled="loading"
                        @keyup.enter="doLogin()"
                    /></ValidationProvider>
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('App.username')"
                      rules="required"
                    >
                      <v-text-field
                        name="account_user_id"
                        prepend-icon="fa-user"
                        type="text"
                        id="account_user_id"
                        v-model="login.account_user_id"
                        :label="$t('App.username')"
                        :error-messages="login.usernameErrors.concat(errors)"
                        :disabled="loading"
                        @keyup.enter="doLogin()"
                    /></ValidationProvider>
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('App.password')"
                      rules="required"
                    >
                      <v-text-field
                        name="password"
                        prepend-icon="fa-lock"
                        id="password"
                        v-model="login.password"
                        :label="$t('App.password')"
                        :error-messages="login.passwordErrors.concat(errors)"
                        :disabled="loading"
                        @keyup.enter="doLogin()"
                        :append-icon="showPassword ? 'fa-eye' : 'fa-eye-slash'"
                        :type="showPassword ? 'text' : 'password'"
                        @click:append="showPassword = !showPassword"
                    /></ValidationProvider>
                  </v-form>
                </ValidationObserver>
              </v-card-text>
              <v-card-actions class="px-4">
                <v-checkbox dense v-model="remember" class="mt-n10">
                  <template v-slot:label>
                    <span class="caption">{{ $t("App.Remember") }}</span>
                  </template>
                </v-checkbox>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click.stop="doLogin()">{{
                  $t("App.loginBtn")
                }}</v-btn>
              </v-card-actions>
              <v-progress-linear rounded :indeterminate="loading" />
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <v-footer v-if="showLoginDialog" inset app absolute color="transparent">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="4"></v-col>
          <v-col
            cols="12"
            sm="8"
            md="4"
            :align="$vuetify.rtl ? 'left' : 'right'"
          >
            <v-menu open-on-hover top offset-y class="text-center">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined v-bind="attrs" v-on="on">{{
                  $t("langTitle")
                }}</v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="lang in langs"
                  :key="lang.key"
                  @click="locale = lang.key"
                >
                  <v-list-item-title>{{ lang.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <span class="ms-3">
              <v-btn
                icon
                v-if="!$vuetify.theme.dark"
                @click.stop="toggleDark()"
              >
                <v-icon>fa-sun</v-icon>
              </v-btn>
              <v-btn icon v-else @click.stop="toggleDark()">
                <v-icon>fa-moon</v-icon>
              </v-btn>
            </span>
          </v-col>
        </v-row>
      </v-footer>
    </v-main>

    <Footer />
  </v-app>
  <v-app v-else-if="accessToken" id="yywd-front">
    <head>
      <title>{{ pageTitle }}</title>
    </head>
    <v-navigation-drawer
      v-if="!printMode"
      class="hidden-print"
      v-model="primaryDrawer.model"
      :clipped="primaryDrawer.clipped"
      :mini-variant="primaryDrawer.mini"
      app
      overflow
      dark
      overlay-opacity="0"
      color="sidebar"
      :right="$vuetify.rtl"
    >
      <Nav
        :settingMode="settingMode"
        :login="login"
        :locale="locale"
        @backToHome="
          settingMode = false;
          $route.path != '/' ? $router.push('/') : null;
        "
      />

      <template v-slot:append>
        <v-text-field
          v-if="$store.state.searchDistrict"
          class="mx-4"
          v-model="districts_search"
          :label="$t('District.search')"
          flat
          hide-details
          clearable
        ></v-text-field>
        <v-sheet
          class="transparent-background-color overflow-y-auto"
          style="max-height: calc(65vh - 180px)"
        >
          <v-treeview
            v-if="$store.state.searchDistrict"
            v-model="tree"
            :items="districts"
            :search="districts_search"
            :filter="districts_filter"
            :open.sync="districts_open"
            :active.sync="districts_active"
            item-key="id"
            item-children="districts"
            dense
            shaped
            hoverable
            activatable
            return-object
          />
        </v-sheet>
        <v-divider />
        <v-container class="d-none">
          <v-row class="px-4 py-2">
            <v-menu dark open-on-hover top offset-y class="text-center">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined v-bind="attrs" v-on="on">{{
                  $t("langTitle")
                }}</v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="lang in langs"
                  :key="lang.key"
                  @click="locale = lang.key"
                >
                  <v-list-item-title>{{ lang.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-spacer />
            <v-btn icon v-if="!$vuetify.theme.dark" @click.stop="toggleDark()">
              <v-icon>fa-sun</v-icon>
            </v-btn>
            <v-btn icon v-else @click.stop="toggleDark()">
              <v-icon>fa-moon</v-icon>
            </v-btn>
          </v-row>
        </v-container>
      </template>
    </v-navigation-drawer>

    <v-app-bar
      v-if="!printMode"
      class="hidden-print"
      :clipped-left="primaryDrawer.clipped"
      :clipped-right="primaryDrawer.clipped"
      app
      dense
      flat
      color="topbar"
    >
      <v-app-bar-nav-icon
        @click.stop="primaryDrawer.model = !primaryDrawer.model"
        color="navIcon"
      ></v-app-bar-nav-icon>
      <v-toolbar-title>
        <v-img
          v-if="!logo_url"
          :class="primaryDrawer.model ? 'd-flex d-lg-none' : ''"
          style="width: 100px; height: 48px"
          :src="require('./assets/' + $LogoFolder + $t('$assets.fullLogo'))"
          contain
        />
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <span class="me-6 d-none d-md-flex subtitle-2 secondary--text">{{
        $t("welcomeMessage", [
          accountUser ? accountUser.name : "",
          accountInfo ? accountInfo.account_name : "",
        ])
      }}</span>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="d-none"
            icon
            v-bind="attrs"
            v-on="on"
            color="navIcon"
            @click.stop="settingMode = !settingMode"
          >
            <v-icon small>fa-cogs</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("App.settings") }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="me-2"
            @click.stop="doLogout()"
            color="navIcon"
          >
            <v-icon small>fa-sign-out-alt</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("App.logout") }}</span>
      </v-tooltip>
      <v-divider vertical />
      <v-menu open-on-hover offset-y class="text-center">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            v-bind="attrs"
            v-on="on"
            class="d-flex d-sm-none ms-1"
            color="grey darken-2"
          >
            <v-icon>fa-language</v-icon>
          </v-btn>
          <v-btn
            text
            v-bind="attrs"
            v-on="on"
            class="d-none d-sm-flex ms-2"
            color="grey darken-2"
          >
            <v-icon x-small class="pe-2 pt-1">fa-language</v-icon
            >{{ $t("langName") }}
            <v-icon x-small class="ps-1">fa-caret-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="lang in langs"
            :key="lang.key"
            @click="locale = lang.key"
          >
            <v-list-item-title>{{ lang.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn icon v-if="!$vuetify.theme.dark" @click.stop="toggleDark()">
        <v-icon small>fa-sun</v-icon>
      </v-btn>
      <v-btn icon v-else @click.stop="toggleDark()">
        <v-icon small>fa-moon</v-icon>
      </v-btn>
    </v-app-bar>

    <Polling />

    <v-main id="app-main">
      <v-progress-linear
        v-if="loading"
        :active="loading"
        :indeterminate="loading"
        absolute
        top
        color="primary"
      />
      <v-row
        no-gutters
        dense
        v-if="printMode"
        class="py-4 px-8 hidden-print"
        style="
          position: absolute;
          top: 0px;
          left: 0px;
          right: 0px;
          background-color: rgba(255, 255, 255, 0.6) !important;
          z-index: 2 !important;
        "
      >
        <v-col cols="2">
          <v-select
            v-model="printPage"
            :items="$t('$print.pageNames')"
            dense
            solo
            hide-details
          />
        </v-col>
        <v-spacer />
        <v-btn dark color="grey" class="me-2 px-6" @click.stop="$router.go(-1)">
          <v-icon x-small class="pe-1">fa-reply</v-icon>
          {{ $t("CRUD.back") }}
        </v-btn>
        <v-btn dark color="print" class="px-6" v-print>
          <v-icon x-small class="pe-1">fa-print</v-icon>
          {{ $t("Print.print") }}
        </v-btn>
      </v-row>
      <v-sheet
        id="main-content"
        :color="printMode ? '' : 'topbar'"
        :class="
          printMode
            ? 'app-overflow-y-scroll app-fill-height'
            : 'app-overflow-y-scroll app-fill-content-height'
        "
        @scroll="scrollEvent()"
      >
        <router-view
          :style="
            printMode
              ? 'margin-left:auto;margin-right:auto;width:' +
                printPageOptions[printPageSize].width +
                'px;'
              : ''
          "
          :class="printMode ? 'print-layout-container' : ''"
          @change-district="changeDistrict"
          @to-parent-district="toParentDistrict"
        ></router-view>
        <v-data-table v-if="printMode" hidden />
      </v-sheet>
      <v-fab-transition>
        <v-btn
          v-if="toTopActive"
          color="accent"
          class="hidden-print"
          fab
          fixed
          bottom
          :left="$vuetify.rtl"
          :right="!$vuetify.rtl"
          @click.stop="toTop()"
        >
          <v-icon>fa-angle-up</v-icon>
        </v-btn>
      </v-fab-transition>
    </v-main>

    <Footer v-if="!printMode" />

    <v-overlay
      :value="$store.state.fullscreenLoading"
      style="z-index: 999999 !important"
    >
      <v-progress-circular
        size="50"
        color="accent"
        indeterminate
      ></v-progress-circular>
    </v-overlay>
    <v-overlay
      opacity="0.67"
      v-if="$store.state.fullscreenImages"
      style="z-index: 999999 !important"
    >
      <v-carousel
        height="100%"
        hide-delimiters
        :show-arrows="
          $store.state.fullscreenImages &&
          $store.state.fullscreenImages.length > 1
        "
      >
        <v-carousel-item
          v-for="(item, i) in $store.state.fullscreenImages"
          :key="i"
          class="fullscreen-images"
          @click="$showFullscreenImages(null)"
        >
          <v-img :src="item" contain height="100%" />
        </v-carousel-item>
      </v-carousel>
    </v-overlay>
  </v-app>
</template>

<script>
import Polling from "./components/Polling";
import Nav from "./Nav";
import Footer from "./Footer";
import i18n from "./i18n";
import { mapState } from "vuex";
import Auth from "@/api/authorizations";
import AccountUsers from "@/api/account_users";
import Districts from "@/api/districts";
import Cities from "@/api/cities";
import { localize, ValidationObserver, ValidationProvider } from "vee-validate";
if (localStorage.getItem("locale"))
  i18n.locale = localStorage.getItem("locale");

export default {
  name: "App",

  components: {
    Polling,
    Nav,
    Footer,
    ValidationObserver,
    ValidationProvider,
  },

  computed: {
    from() {
      return this.$route
        ? this.$route.params
          ? this.$route.params.from
          : ""
        : "";
    },
    districts_filter() {
      return (item, districts_search, textKey) =>
        item[textKey].indexOf(districts_search) > -1;
    },
    appTitle() {
      switch (this.$LogoFolder) {
        case "fengputech/":
          return "蜂铺科技";
      }
      return this.$t("App.title");
    },
    pageTitle() {
      switch (this.$LogoFolder) {
        case "fengputech/":
          return "蜂铺科技";
      }
      return this.$t("pageTitle");
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState([
      "loading",
      "showSearchPanel",
      "showSparklinePanel",
      "fixHeaderHeightInPx",
      "mainContentScrolled",
      "printPageSize",
      "printMode",
      "accessToken",
      "accountInfo",
      "accountUser",
      "permission_districts",
      "serverDistricts",
      "districtID",
    ]),
  },

  data: () => ({
    logo_url: null,
    showLoginDialog: false,
    toTopActive: false,
    primaryDrawer: {
      model: true,
      type: "Default (no property)",
      clipped: false,
      mini: false,
    },
    showPassword: false,
    login: {
      account_id: "",
      account_user_id: "",
      password: "",
      accountErrors: [],
      usernameErrors: [],
      passwordErrors: [],
    },
    langs: [
      { title: "简体中文", key: "zhHans" },
      // { title: "English", key: "en" },
      // { title: "Español", key: "es" },
    ],
    locale: "",
    remember: true,
    tree: [],
    districts: [
      {
        id: 0,
        name: i18n.t("District.all"),
        districts: [],
      },
    ],
    districts_search: null,
    districts_open: [],
    districts_active: [{ id: 0 }],
    printPage: i18n.t("$print.defaultPageOption"),
    printPageOptions: i18n.t("$print.pageOptions"),
    settingMode: false,
  }),

  watch: {
    $route(to) {
      this.logo_url = localStorage.getItem("logo_url");

      // 打印模式切换↓
      this.$store.commit("printMode", to.path.startsWith("/print/"));
      if (this.printMode) {
        var printPageSize = localStorage.getItem("printPageSize");
        printPageSize = printPageSize
          ? printPageSize
          : i18n.t("$print.defaultPageOption");
        this.$store.commit("printPageSize", printPageSize);
        localStorage.setItem("printPageSize", printPageSize);
        this.printPage = printPageSize;
      }
    },
    mainContentScrolled: {
      handler() {
        this.toTopActive = this.mainContentScrolled > 100;
        if (this.toTopActive) this.$store.commit("closeSearchPanel");
        else if (
          this.fixHeaderHeightInPx > 100 &&
          this.mainContentScrolled == 0
        )
          this.$store.commit("openSearchPanel");
      },
    },
    showSearchPanel: {
      handler() {
        this.$setIntervalMainResizer();
      },
    },
    showSparklinePanel: {
      handler() {
        this.$setIntervalMainResizer();
      },
    },
    showBarEchartsPanel: {
      handler() {
        this.$setIntervalMainResizer();
      },
    },
    printPage: {
      handler() {
        this.$store.commit("printPageSize", this.printPage);
        localStorage.setItem("printPageSize", this.printPage);
      },
    },
    printMode: {
      immediate: true,
      handler() {
        if (this.printMode) this.$vuetify.theme.dark = false;
        else {
          if (
            localStorage.getItem("dark") &&
            localStorage.getItem("dark") != "false"
          )
            this.$vuetify.theme.dark = true;
          else this.$vuetify.theme.dark = false;
        }
      },
    },
    locale: {
      handler() {
        this.$i18n.locale = this.locale;
        localize({ en: i18n.t("$validate") }); // validate语言本地化
        localStorage.setItem("locale", this.locale);
      },
    },
    from: {
      // immediate: true,
      handler() {
        if (this.from) {
          this.$store.commit('LOGIN', this.$route.params.access_token)
          this.$router.replace({
            path: this.$route.params.redirect ? this.$route.params.redirect : "/",
            query: this.$route.query
          });
          // Auth.login({
          //   from: this.from,
          //   access_token: this.$route.params.access_token,
          // }).then(
          //     function () {
          //       this.$router.replace({
          //         path: this.$route.params.redirect ? this.$route.params.redirect : "/",
          //         query: this.$route.query
          //       });
          //     }.bind(this)
          // ); // 尝试从跳转参数from和accessToken换取登录状态
        }
      },
    },
    accessToken: {
      // immediate: true,
      handler() {
        if (this.accessToken) {
          AccountUsers.my();
          Districts.tree();
          Cities.fetchMap();
        } else if (this.$route.path != "/") {
          this.$router.push("/");
        }
      },
    },
    districts_active: {
      handler() {
        if (this.districts_active.length > 0)
          this.$store.commit("districtID", this.districts_active[0].id);
      },
    },
    districtID: {
      handler() {
        this.districts_open = [{ id: 0 }]; //重置tree open
        this.openDistrictsTree(this.districts, this.districtID);
        this.districts_active = [{ id: this.districtID }];
      },
    },
    serverDistricts: {
      handler() {
        this.districts[0].districts = this.getDistrictsFiltered(
          this.serverDistricts
        );
        // this.districts_open = [{ id: 0 }];//分组搜索栏默认收起还是默认打开
      },
    },
    primaryDrawer: {
      immediate: true,
      deep: true,
      handler() {
        this.$store.commit("primaryDrawer", this.primaryDrawer.model);
      },
    },
  },

  mounted() {
    this.$store.commit("districtID", 0);
  },

  created() {
    this.locale = this.$i18n.locale;
    if (localStorage.getItem("dark") && localStorage.getItem("dark") != "false")
      this.$vuetify.theme.dark = true;
    this.login.account_id = localStorage.getItem("login.account_id");
    this.login.account_user_id = localStorage.getItem("login.account_user_id");
    if (localStorage.getItem("accessToken")) {
      Auth.check().then(
        function () {
          this.showLoginDialog = !this.accessToken;
        }.bind(this)
      ); // 检查本地accessToken的登录状态
    } else {
      this.showLoginDialog = true;
    }
  },

  methods: {
    scrollEvent() {
      this.$store.commit(
        "mainContentScrolled",
        document.getElementById("main-content").scrollTop
      );
    },
    toTop() {
      this.$scrollTop(); //返回顶部
    },
    toggleDark() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      localStorage.setItem("dark", this.$vuetify.theme.dark);
    },
    doLogin() {
      if (this.loading) return;
      if (
        !this.login.account_id ||
        !this.login.account_user_id ||
        !this.login.password
      )
        return;
      this.login.accountErrors = [];
      this.login.usernameErrors = [];
      this.login.passwordErrors = [];
      Auth.login({
        account_id: this.login.account_id,
        account_user_id: this.login.account_user_id,
        password: this.login.password,
      }).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.showLoginDialog = false;
            if (this.remember) {
              localStorage.setItem("login.account_id", this.login.account_id);
              localStorage.setItem(
                "login.account_user_id",
                this.login.account_user_id
              );
            } else {
              localStorage.removeItem("login.account_id");
              localStorage.removeItem("login.account_user_id");
            }
          } else if (response.data.error.code == "ERR020") {
            //品牌校验失败
            this.login.accountErrors = [response.data.error.msg];
          } else if (response.data.error.code == "ERR002") {
            //账号密码错误
            this.login.usernameErrors = [response.data.error.msg];
            this.login.passwordErrors = [response.data.error.msg];
          } else {
            //其他错误（包括ERR001登录失败）
            this.login.passwordErrors = [response.data.error.msg];
          }
        }.bind(this)
      );
    },
    doLogout() {
      Auth.logout();
      this.showLoginDialog = true;
    },
    getDistrictsFiltered(districts) {
      if (!this.permission_districts || this.permission_districts.length == 0)
        return districts;
      return districts.filter((district) => {
        district.districts = this.getDistrictsFiltered(district.districts); // 递归下级地区
        if (
          (district.districts && district.districts.length > 0) || // 有下级分类的
          this.permission_districts.find((d) => d == district.code) // 和自身有权限的
        )
          return true; // 符合筛选条件
      });
    },
    openDistrictsTree(tree, nodeId) {
      for (var i = 0; i < tree.length; i++) {
        let obj = tree[i];
        if (obj.id == nodeId) {
          // console.log("return obj" + obj.id);
          this.districts_open.push(obj);
          return obj; //找到就结束递归
        } else if (obj.districts && obj.districts.length > 0) {
          //有下层就递归往下一层找
          let node = this.openDistrictsTree(obj.districts, nodeId);
          if (node) {
            this.districts_open.push(obj);
            // console.log("return node" + node.id);
            return node;
          }
        }
      }
      // console.log("return null" + tree.id);
      return null;
    },
    findDistrictNode(tree, nodeId) {
      for (var i = 0; i < tree.length; i++) {
        let obj = tree[i];
        if (obj.id == nodeId) {
          return obj; //找到就结束递归
        } else if (obj.districts && obj.districts.length > 0) {
          //有下层就递归往下一层找
          let node = this.openDistrictsTree(obj.districts, nodeId);
          if (node) {
            return node;
          }
        }
      }
      return null;
    },
    changeDistrict(district) {
      if (district) this.$store.commit("districtID", district.id);
    },
    toParentDistrict() {
      if (this.districtID) {
        let district = this.findDistrictNode(this.districts, this.districtID);
        if (district)
          this.$store.commit(
            "districtID",
            district.parent_id ? district.parent_id : 0
          );
      }
    },
  },

  beforeDestroy() {
    this.$store.dispatch("threadLoadingStopAll");
    this.$store.dispatch("fullscreenLoadingStop");
  },
};
</script>